export const formatApiErrorDetail = (data) => {
  if (data == null) {
    return '';
  }
  if (typeof data === 'string') {
    return data;
  }
  if (typeof data === 'number' || typeof data === 'boolean') {
    return String(data);
  }
  if (Array.isArray(data)) {
    return data.map(formatApiErrorDetail).filter(Boolean).join(', ');
  }
  if (typeof data === 'object') {
    if (typeof data.message === 'string') {
      return data.message;
    }
    if (typeof data.error === 'string') {
      return data.error;
    }
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    const parts = Object.entries(data)
      .map(([key, value]) => {
        const formatted = formatApiErrorDetail(value);
        return formatted ? `${key}: ${formatted}` : null;
      })
      .filter(Boolean);
    if (parts.length > 0) {
      return parts.join('; ');
    }
    try {
      return JSON.stringify(data);
    } catch {
      return '';
    }
  }
  return '';
};

export const getApiErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  const detail = formatApiErrorDetail(error?.response?.data);
  if (status && detail) {
    return `${fallback} (${status}): ${detail}`;
  }
  if (status) {
    return `${fallback} (${status})`;
  }
  if (detail) {
    return `${fallback}: ${detail}`;
  }
  if (error?.message) {
    return `${fallback}: ${error.message}`;
  }
  return fallback;
};

export const normalizeList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data != null && typeof data === 'object') {
    return [data];
  }
  return [];
};

export const getUserId = (user) => {
  const id = user?.web_user_id ?? user?.user_id ?? user?.id;
  return id != null ? Number(id) : null;
};

const parseTruthyFlag = (value) => {
  if (value === true || value === 1) {
    return true;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

/** Flattens nested web_user / user objects from API responses */
export const normalizeWebUserRecord = (record) => {
  if (!record || typeof record !== 'object') {
    return record ?? {};
  }

  const nested = record.web_user ?? record.user ?? record.data?.web_user ?? record.data?.user;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...record, ...nested };
  }

  return record;
};

const BUILTIN_TYPE_PATTERN = /built[-_\s]?in|ugrađen|ugraden/i;

const normalizeRoleNameForMatch = (name) =>
  String(name)
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212]/g, '-')
    .replace(/\s+/g, ' ');

export const isBuiltInRoleName = (name) => {
  if (name == null || String(name).trim() === '') {
    return false;
  }

  const normalized = normalizeRoleNameForMatch(name);

  return (
    normalized.includes('built-in') ||
    normalized.includes('builtin') ||
    normalized.includes('ugrađen') ||
    normalized.includes('ugraden') ||
    BUILTIN_TYPE_PATTERN.test(normalized)
  );
};

/** Same role label logic as the users table */
export const getResolvedRoleName = (user, role, rolesCatalog = []) => {
  const record = normalizeWebUserRecord(user);
  const roleObj = role && typeof role === 'object' ? role : null;

  const candidates = [
    roleObj?.name,
    roleObj?.role_name,
    record.role?.name,
    record.role_name,
    typeof record.role === 'string' ? record.role : null,
  ];

  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return String(candidate).trim();
    }
  }

  const roleId = normalizeRoleId(
    roleObj?.role_id ?? record.role_id ?? record.fk_role_id
  );

  if (roleId != null && rolesCatalog.length > 0) {
    const fromCatalog = rolesCatalog.find((item) => Number(item.role_id) === roleId);
    if (fromCatalog?.name) {
      return String(fromCatalog.name).trim();
    }
  }

  return null;
};

const hasBuiltInFlag = (record) => {
  if (!record || typeof record !== 'object') {
    return false;
  }

  return ['built_in', 'builtIn', 'is_built_in', 'isBuiltIn'].some((key) =>
    parseTruthyFlag(record[key])
  );
};

/** System roles (is_system: true) must not appear in assign-role dropdown */
export const isNonAssignableSystemRole = (role) => {
  if (!role || typeof role !== 'object') {
    return false;
  }

  const isSystem = role.is_system ?? role.role?.is_system ?? null;
  return parseTruthyFlag(isSystem);
};

export const mergeWebUserWithRole = (user, role) => {
  const merged = normalizeWebUserRecord(user);
  if (!role || typeof role !== 'object') {
    return merged;
  }

  const roleName = role.name ?? role.role_name ?? null;
  const builtIn =
    hasBuiltInFlag(merged) ||
    hasBuiltInFlag(role) ||
    isBuiltInRoleName(roleName) ||
    isBuiltInRoleName(merged.role_name);

  return {
    ...merged,
    ...(roleName
      ? {
          role_name: roleName,
          role: {
            ...(merged.role && typeof merged.role === 'object' ? merged.role : {}),
            name: roleName,
          },
        }
      : {}),
    ...(role.system_role_type && !merged.system_role_type
      ? { system_role_type: role.system_role_type }
      : {}),
    ...(builtIn ? { built_in: true, builtIn: true } : {}),
  };
};

export const getSystemRoleType = (user) => {
  const record = normalizeWebUserRecord(user);
  if (!record || typeof record !== 'object') {
    return null;
  }

  const type =
    record.system_role_type ??
    record.role?.system_role_type ??
    (Array.isArray(record.roles) ? record.roles[0]?.system_role_type : null);

  if (type == null || String(type).trim() === '') {
    return null;
  }

  return String(type);
};

export const hasSystemRoleWebUser = (user) => getSystemRoleType(user) != null;

/** System or built-in users cannot be edited, deleted, or have roles changed in UI */
export const isWebUserLockedWithRole = (user, role, rolesCatalog = []) => {
  const merged = mergeWebUserWithRole(user, role);

  if (hasSystemRoleWebUser(merged) || hasBuiltInFlag(merged)) {
    return true;
  }

  return isBuiltInRoleName(getResolvedRoleName(user, role, rolesCatalog));
};

export const isWebUserLocked = (user) => isWebUserLockedWithRole(user, null, []);

export const normalizeRoleId = (roleId) =>
  roleId != null && roleId !== '' ? Number(roleId) : null;

export const parseRoleFromApiData = (data) => {
  if (data == null) {
    return null;
  }

  if (!Array.isArray(data) && typeof data === 'object' && data.role != null) {
    const nested = parseRoleFromApiData(data.role);
    if (nested) {
      return nested;
    }
  }

  const candidates = Array.isArray(data) ? data : [data];

  for (const item of candidates) {
    if (item == null || typeof item !== 'object') {
      continue;
    }

    const roleId = normalizeRoleId(item.role_id ?? item.fk_role_id);
    const name = item.name ?? item.role_name ?? item.role?.name ?? null;
    const systemRoleType =
      item.system_role_type ?? item.role?.system_role_type ?? null;
    const builtIn =
      hasBuiltInFlag(item) ||
      hasBuiltInFlag(item.role) ||
      isBuiltInRoleName(name);

    if (roleId != null) {
      return {
        role_id: roleId,
        name,
        system_role_type: systemRoleType,
        ...(builtIn ? { built_in: true, builtIn: true } : {}),
      };
    }

    if (item.role && typeof item.role === 'object') {
      const nestedId = normalizeRoleId(item.role.role_id ?? item.role.fk_role_id);
      const nestedName = item.role.name ?? item.role.role_name ?? name;
      if (nestedId != null) {
        const nestedBuiltIn =
          hasBuiltInFlag(item.role) ||
          hasBuiltInFlag(item) ||
          isBuiltInRoleName(nestedName);
        return {
          role_id: nestedId,
          name: nestedName,
          system_role_type:
            item.role.system_role_type ?? item.system_role_type ?? null,
          ...(nestedBuiltIn ? { built_in: true, builtIn: true } : {}),
        };
      }
    }

  }

  return null;
};

export const getRoleFromUser = (user, rolesCatalog = []) => {
  if (!user || typeof user !== 'object') {
    return null;
  }

  const parsed = parseRoleFromApiData(user);
  if (parsed) {
    return parsed;
  }

  const roleId = normalizeRoleId(
    user.role_id ??
      user.fk_role_id ??
      (Array.isArray(user.roles) && user.roles.length > 0
        ? user.roles[0].role_id ?? user.roles[0].fk_role_id
        : null)
  );

  if (roleId == null) {
    return null;
  }

  const name =
    user.role?.name ??
    (Array.isArray(user.roles) ? user.roles[0]?.name : null) ??
    rolesCatalog.find((role) => Number(role.role_id) === roleId)?.name ??
    null;

  return { role_id: roleId, name };
};

export const getChangedRoleAssignments = (initial, pending) => {
  const userIds = new Set([
    ...Object.keys(initial).map(Number),
    ...Object.keys(pending).map(Number),
  ]);

  return [...userIds]
    .map((userId) => {
      const initialRoleId = normalizeRoleId(initial[userId]);
      const pendingRoleId = normalizeRoleId(pending[userId]);

      if (initialRoleId === pendingRoleId) {
        return null;
      }

      return { userId, initialRoleId, pendingRoleId };
    })
    .filter(Boolean);
};

export const getRoleAssignmentErrorMessage = (error, action) => {
  const status = error?.response?.status;
  const detail = formatApiErrorDetail(error?.response?.data);
  const statusText = error?.response?.statusText;

  if (status === 400) {
    return `${action}: neispravan zahtev (400)${detail ? ` — ${detail}` : ''}`;
  }
  if (status === 404) {
    return `${action}: nije pronađeno (404)${detail ? ` — ${detail}` : ''}`;
  }
  if (status === 409) {
    return `${action}: konflikt (409)${detail ? ` — ${detail}` : ''}`;
  }
  if (status === 401) {
    return `${action}: niste prijavljeni (401)`;
  }
  if (status === 403) {
    return `${action}: nemate dozvolu (403)${detail ? ` — ${detail}` : ''}`;
  }
  if (status >= 500) {
    return `${action}: greška servera (${status})${detail ? ` — ${detail}` : ''}`;
  }
  if (status) {
    return `${action}: HTTP ${status}${statusText ? ` ${statusText}` : ''}${detail ? ` — ${detail}` : ''}`;
  }
  if (error?.message) {
    return `${action}: ${error.message}`;
  }
  return `${action}: nepoznata greška`;
};
